import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import SportPlayerPicker from "../components/community/SportPlayerPicker";
import RosterSlotGrid from "../components/community/RosterSlotGrid";
import {
  useChallengeCommunityRosterMutation,
  useEnterCommunityChallengeMutation,
  useGetCommunityRosterQuery,
  usePublishCommunityRosterMutation,
  useRemixCommunityRosterMutation,
  useUpdateCommunityRosterMutation,
} from "../store/api/communityApi";
import { useSportWorkspace } from "../hooks/useSportWorkspace";
import { useAnalytics } from "../hooks/useAnalytics";
import { brandFilledButtonSx } from "../utils/brandAssets";
import {
  communityMatchupPath,
  communityRosterPath,
} from "../utils/community";

const CommunityBuild = () => {
  const { sport } = useSportWorkspace();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { trackCustomEvent } = useAnalytics();
  const existingId = params.get("roster");
  const remixId = params.get("remix");
  const versusId = params.get("versus");
  const challengeId = params.get("challenge");

  const [roster, setRoster] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [picker, setPicker] = useState(null);
  const [notice, setNotice] = useState("");
  const [bootstrapped, setBootstrapped] = useState(false);

  const loaded = useGetCommunityRosterQuery(existingId, { skip: !existingId });
  const [remixRoster] = useRemixCommunityRosterMutation();
  const [challengeRoster] = useChallengeCommunityRosterMutation();
  const [enterChallenge] = useEnterCommunityChallengeMutation();
  const [updateRoster] = useUpdateCommunityRosterMutation();
  const [publishRoster, { isLoading: publishing }] =
    usePublishCommunityRosterMutation();

  useEffect(() => {
    if (existingId && loaded.data && !roster) {
      setRoster(loaded.data);
      setTitle(loaded.data.title);
      setDescription(loaded.data.description || "");
      setBootstrapped(true);
    }
  }, [existingId, loaded.data, roster]);

  useEffect(() => {
    if (bootstrapped || existingId) return undefined;
    let cancelled = false;

    const start = async () => {
      try {
        let next = null;
        if (remixId) {
          next = await remixRoster(remixId).unwrap();
          trackCustomEvent("roster_remixed", { sport, source: remixId });
        } else if (versusId) {
          const started = await challengeRoster({ id: versusId }).unwrap();
          next = started.draft;
          trackCustomEvent("matchup_created", { sport, source: versusId });
        } else if (challengeId) {
          const started = await enterChallenge({ id: challengeId }).unwrap();
          next = started.draft;
          trackCustomEvent("challenge_started", { sport, challenge: challengeId });
        }
        if (!cancelled && next) {
          setRoster(next);
          setTitle(next.title);
          setDescription(next.description || "");
          navigate(`${window.location.pathname}?roster=${next.id}`, { replace: true });
        }
        if (!cancelled) setBootstrapped(true);
      } catch (err) {
        if (!cancelled) {
          setNotice(err?.data?.error || "Could not start this roster");
          setBootstrapped(true);
        }
      }
    };

    start();
    return () => {
      cancelled = true;
    };
  }, [
    bootstrapped,
    challengeId,
    challengeRoster,
    enterChallenge,
    existingId,
    navigate,
    remixId,
    remixRoster,
    sport,
    trackCustomEvent,
    versusId,
  ]);

  const excludeIds = useMemo(
    () =>
      Object.values(roster?.players || {})
        .map((player) => player?.playerId)
        .filter(Boolean),
    [roster]
  );

  const persist = async (patch) => {
    if (!roster) return;
    const next = await updateRoster({ id: roster.id, ...patch }).unwrap();
    setRoster(next);
    return next;
  };

  const handlePick = async (player) => {
    try {
      await persist({
        players: { [picker.id]: player.playerId },
      });
      setPicker(null);
    } catch (err) {
      setNotice(err?.data?.error || "Could not update player");
    }
  };

  const handlePublish = async () => {
    try {
      await persist({ title, description });
      const result = await publishRoster(roster.id).unwrap();
      trackCustomEvent("roster_published", { sport, roster_id: roster.id });
      if (result.matchup) {
        navigate(communityMatchupPath(sport, result.matchup.id));
        return;
      }
      navigate(
        communityRosterPath(sport, result.roster?.slug || result.roster?.id)
      );
    } catch (err) {
      setNotice(err?.data?.error || "Could not publish roster");
    }
  };

  if (!bootstrapped || (existingId && loaded.isLoading)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!roster) {
    return (
      <Alert severity="info" sx={{ mt: 4 }}>
        Start from a public roster, remix, or today’s challenge.
      </Alert>
    );
  }

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, maxWidth: 1280, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: "0.16em" }}
          >
            Build a roster
          </Typography>
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
            {roster.inspiredByLabel
              ? "Your version"
              : roster.challengeSourceRosterId
              ? "Your challenge"
              : roster.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 520 }}>
            {roster.inspiredByLabel
              ? `Inspired by ${roster.inspiredByLabel}`
              : "Tap the field. Publish when the roster is complete."}
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={brandFilledButtonSx}
          disabled={publishing || !roster.complete}
          onClick={handlePublish}
        >
          {roster.challengeSourceRosterId
            ? "Publish and create matchup"
            : "Publish roster"}
        </Button>
      </Box>
      <Box sx={{ display: "grid", gap: 2, mb: 4, maxWidth: 520 }}>
        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={() => persist({ title }).catch(() => {})}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          onBlur={() => persist({ description }).catch(() => {})}
          multiline
          minRows={2}
        />
      </Box>
      <RosterSlotGrid
        roster={roster}
        sport={roster.sport}
        editable
        onSelect={setPicker}
        onClear={(slot) => persist({ players: { [slot.id]: null } })}
      />
      <SportPlayerPicker
        open={Boolean(picker)}
        sport={roster.sport}
        title={picker ? `Pick ${picker.label}` : "Pick a player"}
        slotId={picker?.id}
        excludePlayerIds={excludeIds.filter(
          (id) => id !== roster.players?.[picker?.id]?.playerId
        )}
        onClose={() => setPicker(null)}
        onSelect={handlePick}
      />
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice("")}
        message={notice}
      />
    </Box>
  );
};

export default CommunityBuild;
